import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '~src/authentication/serverApi/requests/GeneratePublicKeyRequest';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';

import { IListFetcher } from '../../../httpClient/fetchers/IListFetcher';
import { ListResult } from '../../../httpClient/fetchers/ListResult';
import { Resource } from '../../../httpClient/resources/Resource';
import { IResourceQuery } from '../../../httpClient/resources/ResourceQuery';
import { IPagedItemsParams } from '../../../httpClient/serverApi/types/IPage';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import { SliceableHelper } from '../../../httpClient/utils/SliceableHelper';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { FilterFieldNames, IFieldFilterClause } from '../../../spacesPlatform/models/IFilterClause';
import { ISiteUserInfo } from '../../users/models/ISiteUserInfo';
import { AddUsersToSiteRequest } from '../../users/serverApi/requests/AddUsersToSiteRequest';
import { CreateLocalUserRequest } from '../../users/serverApi/requests/CreateLocalUserRequest';
import { CreateLocalUserWithSiteSamlAuthRequest } from '../../users/serverApi/requests/CreateLocalUserWithSiteSamlAuthRequest';
import { DeleteUsersRequest } from '../../users/serverApi/requests/DeleteUsersRequest';
import { GetRemainingUserQuotaRequest, IGetRemainingUserQuotaResult } from '../../users/serverApi/requests/GetRemainingUserQuotaRequest';
import { GetSiteUsersRequest } from '../../users/serverApi/requests/GetSiteUsersRequest';
import { ImportExternalSiteUsersRequest } from '../../users/serverApi/requests/ImportExternalUsers';
import { ImportSiteUsersFromActiveDirectoryRequest } from '../../users/serverApi/requests/ImportUsersFromActiveDirectoryRequest';
import { UpdateUsersAdminStatusRequest } from '../../users/serverApi/requests/UpdateUsersAdminStatusRequest';
import { UpdateUsersGroupMembershipRequest } from '../../users/serverApi/requests/UpdateUsersGroupMembershipRequest';
import { UpdateUsersSiteRoleRequest } from '../../users/serverApi/requests/UpdateUsersSiteRoleRequest';
import { SiteRoles } from '../../users/serverApi/types/ISiteRole';
import { IUserActionResult } from '../../users/serverApi/types/IUserActionResult';
import { SiteRolesResource } from './SiteRoles';


function fetchList(params: IPagedItemsParams): Promise<ListResult<ISiteUserInfo>> {
  let siteUserFetcher: IListFetcher<ISiteUserInfo> = new WebClientListResourceFetcher(GetSiteUsersRequest, ServerApiMapper.siteUsers);

  if (!SiteAuthenticationSettingsService.isEnabled()) {
    return siteUserFetcher.fetchList(params);
  }
  return Promise.all([
    siteUserFetcher.fetchList(params),
    SiteAuthenticationSettingsService.getAuthSettingToProviderIdMap()])
  .then((results: any[]) => {
    const userList: ListResult<ISiteUserInfo> = results[0];
    const map: _.Dictionary<string> = results[1];

    _.each(userList.items, (siteUser: ISiteUserInfo) => {
      siteUser.authenticationProviderId = map[siteUser.authSetting];
    });
    return userList;
  });
}

export const AuthenticationProviderIdFetcher = {fetchList};

export class SiteUsers extends Resource<ISiteUserInfo>{
  public static readonly instance = new SiteUsers(AuthenticationProviderIdFetcher);

  // gets the user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): Promise<any> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  public getUsersById(userIds: string[]): Promise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray('id', userIds)));
  }

  public getUsersByGroupIds(groupIds: string[]): Promise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray('groupIds', groupIds)));
  }

  // gets users with the specified siteRoles.
  public getUsersBySiteRoles(siteRoles: string[]): Promise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray(FilterFieldNames.siteRole, siteRoles)));
  }

  // group membership - remove only
  public removeFromGroups(userIds: string[], groupIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: [],
          removeFromGroupIds: groupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  public getUserCountsBySiteRole(baseQuery: IResourceQuery<ISiteUserInfo>): Promise<_.Dictionary<number>> {

    // remove any existing site role filter
    const filtersWithoutSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.siteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutSiteRole);

    const counts: _.Dictionary<number> = {};

    const allSiteRolesExceptGuest = _.without(SiteRolesResource.instance.allOptions(), SiteRoles.Guest);

    const promises = _.map(allSiteRolesExceptGuest, (siteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
        .getCount()
        .then((totalCount: number) => {
          counts[siteRole] = totalCount;
        });
    });

    //Any site role
    promises.push(modifiedQuery.getCount().then((totalCount: number) => {counts['Any'] = totalCount; }));
    return Promise.all(promises).then(() => counts);
  }

  // group membership - add only
  public addToGroups(userIds: string[], groupIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: groupIdsBlock,
          removeFromGroupIds: []
        })),
      ResultHelper.combineResults);
  }

  // group membership - add and remove
  public updateUsersGroupMembership(userIds: string[], addToGroupIds: string[], removeFromGroupIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToGroupIds, removeFromGroupIds,
        (userIdsBlock, addToGroupIdsBlock, removeFromGroupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: addToGroupIdsBlock,
          removeFromGroupIds: removeFromGroupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  // creates a local user
  public createLocalUser(
    username: string,
    displayName: string,
    email: string,
    password: string,
    identityPoolName: string,
    admin: boolean,
    siteRole: string,
    identifier: string
  ): Promise<IUserActionResult> {
    return WebHttpClient.sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalUserRequest = new CreateLocalUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId,
          identityPoolName: identityPoolName,
          admin: admin,
          siteRole: siteRole,
          identifier: identifier
        });
        return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalUserRequest);
      });
  }

  // creates a local SiteSAML user
  public createLocalUserWithSamlOrSiteSamlAuth(
    username: string,
    displayName: string,
    email: string,
    authenticationProviderId: string,
    identityPoolName: string,
    admin: boolean,
    siteRole: string,
    identifier: string
  ): Promise<IUserActionResult> {
    return SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = SiteAuthenticationSettingsService.getAuthSetting(authenticationProviderId, settings);
      const createLocalUserWithSiteSamlAuthRequest = new CreateLocalUserWithSiteSamlAuthRequest({
            username: username,
            displayName: displayName,
            email: email,
            authSetting: authSetting,
            identityPoolName: identityPoolName,
            admin: admin,
            siteRole: siteRole,
            identifier: identifier
          });
      return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalUserWithSiteSamlAuthRequest);
    });
  }

  // adds users to this site
  public addUsersToSite(usernames: string[], siteRole: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames, (usernamesBlock) => new AddUsersToSiteRequest(usernamesBlock, siteRole)),
      ResultHelper.combineResults);
  }

  // delete users from this site
  public deleteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteUsersRequest(userIdsBlock)),
      ResultHelper.combineUserResults);
  }

  // imports users from active directory to this site
  public importUsersFromActiveDirectory(userIds: string[], identityPoolName: string, siteRole: string, serverAdmin: boolean = false): Promise<IUserActionResult> {
    let requestBuilder: (userIds: string[]) => ImportSiteUsersFromActiveDirectoryRequest;
    if (serverAdmin) {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, identityPoolName: identityPoolName, admin: true });
    } else {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, identityPoolName: identityPoolName, siteRole: siteRole });
    }
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, requestBuilder),
      ResultHelper.combineUserResults);
  }

  public importExternalUsers(emails: string[], siteRole: string, authenticationProviderId: string): Promise<IUserActionResult> {
    return SiteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = SiteAuthenticationSettingsService.getAuthSetting(authenticationProviderId, settings);
      return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests(emails, (emailsBlock) => new ImportExternalSiteUsersRequest({
          emailAddresses: emailsBlock,
          siteRole: siteRole,
          authSetting: authSetting
        })),
        ResultHelper.combineUserResults);
    });
  }

  // updates users site role for this site
  public updateUsersSiteRole(userIds: string[], siteRole: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersSiteRoleRequest({
        userIds: userIdsBlock,
        siteRole: siteRole
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. Includes SOLR delay.
  public updateServerAdminStatusAndDelay(userIds: string[], admin: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. No SOLR delay.
  public updateServerAdminStatusNoDelay(userIds: string[], admin: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  public getRemainingUserQuota(): Promise<number> {
    return WebHttpClient.sendRequest(new GetRemainingUserQuotaRequest())
      .then((result: IGetRemainingUserQuotaResult) => result.usersRemaining);
  }
}

export default SiteUsers.instance;
